import React from 'react';
import { ArrowRight, Play, Star, CheckCircle2 } from 'lucide-react';
import { stats, images } from '../data/mock';

const Hero = () => {
  return (
    <section id="beranda" className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 -left-40 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-orange-400/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }} />
        
        {/* Floating Shapes */}
        <div className="absolute top-20 left-10 w-4 h-4 bg-orange-400/40 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }} />
        <div className="absolute top-40 right-20 w-3 h-3 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-40 left-1/4 w-2 h-2 bg-orange-300/50 rounded-full animate-bounce" style={{ animationDelay: '1.5s' }} />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 animate-fade-in">
              <Star className="w-4 h-4 text-orange-400 fill-orange-400" />
              <span className="text-white/90 text-sm font-medium">Kursus dan Bimbingan Belajar Terpercaya</span>
            </div>

            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                <span className="inline-block animate-slide-up" style={{ animationDelay: '0.1s' }}>GENIUS</span>
                <br />
                <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-500 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                  EDUCATION
                </span>
                <br />
                <span className="inline-block animate-slide-up" style={{ animationDelay: '0.3s' }}>CENTRE</span>
              </h1>
              <p className="text-xl sm:text-2xl text-orange-300 font-semibold italic animate-fade-in" style={{ animationDelay: '0.4s' }}>
                &ldquo;Mendidik untuk Mencerdaskan&rdquo;
              </p>
            </div>

            {/* Description */}
            <p className="text-lg text-blue-100/80 max-w-xl mx-auto lg:mx-0 animate-fade-in" style={{ animationDelay: '0.5s' }}>
              Lembaga pendidikan terpercaya dengan program lengkap dari PAUD hingga persiapan TOEFL. 
              Didukung tutor profesional dan fasilitas modern.
            </p>

            {/* Features List */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 animate-fade-in" style={{ animationDelay: '0.6s' }}>
              {['Tutor Profesional', 'Kelas Fleksibel', 'Metode Modern'].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-orange-400" />
                  <span className="text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in" style={{ animationDelay: '0.7s' }}>
              <a 
                href="#kontak" 
                className="group px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-orange-500/30 transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
              >
                Daftar Sekarang
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a 
                href="#program" 
                className="group px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white rounded-xl font-bold text-lg hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                Lihat Program
              </a>
            </div>
          </div>

          {/* Right Content - Image Gallery */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main Image */}
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                <img 
                  src={images.teaching}
                  alt="Genius Education Centre - Teaching" 
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 via-transparent to-transparent" />
              </div>

              {/* Floating Card 1 */}
              <div className="absolute -bottom-6 -left-6 z-20 bg-white rounded-2xl p-4 shadow-xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">1000+</p>
                    <p className="text-sm text-gray-500">Siswa Aktif</p>
                  </div>
                </div>
              </div>

              {/* Floating Card 2 */}
              <div className="absolute -top-4 -right-4 z-20 bg-white rounded-2xl p-4 shadow-xl animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">15+</p>
                    <p className="text-sm text-gray-500">Tahun Pengalaman</p>
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -bottom-10 right-10 w-24 h-24 border-4 border-orange-400/30 rounded-full" />
              <div className="absolute top-10 -left-10 w-16 h-16 border-4 border-blue-400/30 rounded-full" />
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 animate-fade-in" style={{ animationDelay: '0.8s' }}>
          {stats.map((stat, idx) => (
            <div 
              key={stat.id} 
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/10 hover:bg-white/20 transition-all duration-300 hover:scale-105"
            >
              <p className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.value}</p>
              <p className="text-blue-200 text-sm font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
